import {
  Button,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  useToast,
} from "@egaranti/components";
import { TableHead } from "@egaranti/components";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import documentsService from "@/services/documentsService";

import { ArrowLeft, CheckCircle } from "lucide-react";

const PAGE_SIZE = 10;

const documentTypes = {
  WARRANTY: {
    id: "WARRANTY_DOCUMENT",
    translationKey: "settingsPage.warrantyDocument",
    idField: "warrantyDocumentId",
  },
  USER_MANUAL: {
    id: "USER_MANUAL_DOCUMENT",
    translationKey: "settingsPage.userManualDocument",
    idField: "userManuelDocumentId",
  },
};

const MissingDocumentsPage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [state, setState] = useState({
    products: [],
    totalPages: 0,
    currentPage: 1,
    loading: false,
    uploading: {},
    uploadedDocs: {},
  });

  const fetchProducts = useCallback(
    async (page = 1) => {
      setState((prev) => ({ ...prev, loading: true }));

      try {
        const response = await documentsService.getMissingDocumentsPaginated(
          page,
          PAGE_SIZE,
        );
        setState((prev) => ({
          ...prev,
          products: response.content || [],
          totalPages: response.totalPages || 1,
          currentPage: page,
        }));
      } catch (error) {
        console.error("Error fetching products:", error);
        toast({
          title: t("settings.documents.missing.fetchError"),
          variant: "error",
        });
      } finally {
        setState((prev) => ({ ...prev, loading: false }));
      }
    },
    [toast, t],
  );

  useEffect(() => {
    fetchProducts(1);
  }, [fetchProducts]);

  const handleFileChange = useCallback(
    async (event, product, documentType) => {
      const file = event.target.files[0];
      if (!file) return;

      const uploadKey = `${product.id}-${documentType}`;
      setState((prev) => ({
        ...prev,
        uploading: { ...prev.uploading, [uploadKey]: true },
      }));

      try {
        const response = await documentsService.uploadDocumentForProduct({
          productId: product.id,
          documentType,
          file,
        });

        setState((prev) => ({
          ...prev,
          uploadedDocs: {
            ...prev.uploadedDocs,
            [uploadKey]: {
              fileName: file.name,
              uploadTime: new Date().toISOString(),
              documentId: response?.data?.id,
            },
          },
        }));

        toast({
          title: t("settingsPage.documentCreatedSuccesfully"),
          variant: "success",
        });
      } catch (error) {
        console.error("Error uploading document:", error);
        toast({
          title: t("settingsPage.documentCreatedFailed"),
          variant: "error",
        });
      } finally {
        setState((prev) => ({
          ...prev,
          uploading: { ...prev.uploading, [uploadKey]: false },
        }));
        event.target.value = null;
      }
    },
    [toast, t],
  );

  const handlePageChange = useCallback(
    (page) => {
      fetchProducts(page);
    },
    [fetchProducts],
  );

  const renderUploadButton = useCallback(
    (product, docType) => {
      const uploadKey = `${product.id}-${docType.id}`;
      const isUploading = state.uploading[uploadKey];
      const uploadInfo = state.uploadedDocs[uploadKey];
      const hasExistingDoc =
        product[docType.idField] ||
        (docType.id === documentTypes.WARRANTY.id &&
          product.warrantyDocumentId) ||
        (docType.id === documentTypes.USER_MANUAL.id &&
          product.userManuelDocumentId);

      if (hasExistingDoc || uploadInfo) {
        return (
          <div className="flex items-center space-x-2 rounded-lg border border-green-100 bg-green-50 px-3 py-2 text-green-700">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <div className="flex flex-col">
              <span className="font-medium">
                {t("settingsPage.documentCreatedSuccesfully")}
              </span>
            </div>
          </div>
        );
      }

      return (
        <label className="block w-full cursor-pointer">
          <input
            type="file"
            accept="application/pdf,image/*"
            className="hidden"
            disabled={isUploading}
            onChange={(e) => handleFileChange(e, product, docType.id)}
          />
          <div
            className={`rounded-lg border border-dashed px-3 py-2 text-center ${
              isUploading
                ? "pointer-events-none cursor-not-allowed bg-gray-100 text-gray-400"
                : "hover:border-blue-600 hover:text-blue-600"
            } transition-all`}
          >
            {isUploading
              ? t("common.loading")
              : t("productsPage.searchDocuments.placeholder")}
          </div>
        </label>
      );
    },
    [state.uploading, state.uploadedDocs, handleFileChange, t],
  );

  const filteredProducts = useMemo(() => {
    return state.products.filter((product) => {
      const needsWarranty =
        !product.warrantyDocumentId &&
        !state.uploadedDocs[`${product.id}-${documentTypes.WARRANTY.id}`];
      const needsManual =
        !product.userManuelDocumentId &&
        !state.uploadedDocs[`${product.id}-${documentTypes.USER_MANUAL.id}`];
      return needsWarranty || needsManual;
    });
  }, [state.products, state.uploadedDocs]);

  if (state.loading) {
    return <div className="p-6">{t("common.loading")}</div>;
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center">
        <Button
          variant="secondaryGray"
          className="mr-4"
          onClick={() => navigate("/settings/documents")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-xl font-semibold">
          {t("settingsPage.missingDocuments")}
        </h1>
      </div>

      {filteredProducts.length === 0 ? (
        <div className="py-8 text-center">
          {t("settings.documents.missing.noDocuments")}
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableHead>{t("productsPage.productName")}</TableHead>
              <TableHead>{t("settingsPage.warrantyDocument")}</TableHead>
              <TableHead>{t("settingsPage.userManual")}</TableHead>
            </TableHeader>
            <TableBody>
              {filteredProducts.map((product) => (
                <TableRow key={product.id}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">{product.name}</span>
                      <span className="text-sm text-gray-500">
                        {product.brand}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="w-[300px]">
                    {renderUploadButton(product, documentTypes.WARRANTY)}
                  </TableCell>
                  <TableCell className="w-[300px]">
                    {renderUploadButton(product, documentTypes.USER_MANUAL)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="mt-4 flex justify-center">
            <Pagination
              currentPage={state.currentPage}
              totalPages={state.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default MissingDocumentsPage;
